<template>
  <div>
    <div class="mb-2 d-flex align-items-center gap-2">
      <router-link
        :to="{
          name: 'sale-prices-additional-fees-selection'
        }"
        class="d-flex align-items-center gap-2"
      >
        <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
        {{ $t('Back to Provider and Customer Selection') }}
      </router-link>
    </div>

    <b-overlay
      variant="white"
      :show="submitting"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
    >
      <validation-observer ref="additionalFeesForm">
        <b-form ref="additionalFeesFormElement" class="" @submit.prevent="submit">
          <b-row>
            <b-col cols="12">
              <div class="additional-fees-list">
                <template v-if="loading">
                  <div v-for="i in 3" :key="i" class="service-item">
                    <b-skeleton width="20%" height="24px" class="mb-0" />
                    <b-skeleton width="20%" height="24px" class="mb-0" />
                    <b-skeleton width="20%" height="24px" class="mb-0" />
                    <b-skeleton width="20%" height="24px" class="mb-0" />
                  </div>
                </template>
                <template v-else-if="additionalFees.length > 0">
                  <div class="service-item-header">
                    <div class="service-name">
                      <label>{{ t('Fee Name') }}</label>
                    </div>
                    <div class="service-price-type">
                      <label>{{ t('Price Type') }}</label>
                    </div>
                    <div class="service-price-value">
                      <label>{{ t('Cost Price Value') }}</label>
                    </div>
                    <div class="service-price-value">
                      <label>{{ t('Sale Price Value') }}</label>
                    </div>
                  </div>
                  <div
                    v-for="fee in additionalFees"
                    :key="fee.provider_additional_fee_id"
                    class="service-item"
                  >
                    <!-- Description (Readonly) -->
                    <div class="service-name">
                      <label>{{ fee.description }}</label>
                    </div>

                    <!-- Price Type (Readonly) -->
                    <div class="service-price-type">
                      <label>{{ fee.pricingType === 1 ? $t('Fixed') : $t('Percentage') }}</label>
                    </div>

                    <!-- Cost Price (Readonly) -->
                    <div class="service-price-value">
                      <b-form-input type="number" :value="fee.price" placeholder="0.00" disabled />
                    </div>

                    <!-- Sale Price Input -->
                    <div class="service-price-value">
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('Sale Price')"
                        rules="required|doubleOrInteger|positive"
                      >
                        <b-form-input
                          type="number"
                          v-model="fee.salePrice"
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </div>

                    <b-alert
                      v-if="fee.salePrice && fee.salePrice < fee.price"
                      variant="warning"
                      show
                      class="mt-1 p-1 w-100"
                      style="grid-column: 1 / -1"
                    >
                      <strong>
                        <feather-icon icon="AlertTriangleIcon" size="14" class="mr-1" />
                        <small class="mr-1 font-weight-bolder">
                          {{ t('Sale price less than cost price is not recommended') }}
                        </small>
                      </strong>
                    </b-alert>
                  </div>
                </template>
                <template v-else>
                  <div class="empty-state text-center p-4">
                    <feather-icon icon="PackageIcon" size="48" class="mb-2" />
                    <h4>{{ t('No Additional Fees') }}</h4>
                    <p class="text-muted">
                      {{ t('There are no additional fees available for this provider.') }}
                    </p>
                    <b-button
                      variant="outline-primary"
                      class="mt-2"
                      @click="$router.push({ name: 'sale-prices-additional-fees-selection' })"
                    >
                      <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
                      {{ t('Back to Selection') }}
                    </b-button>
                  </div>
                </template>
              </div>
            </b-col>

            <!-- Submit Button -->
            <b-col cols="12">
              <div
                class="d-flex gap-3 align-items-center justify-content-end mt-2"
                style="gap: 12px"
              >
                <b-button
                  variant="primary"
                  class="d-flex align-items-center"
                  type="submit"
                  :disabled="additionalFees.length === 0"
                >
                  {{ submitting ? t('Please Wait') : t('Save') }}
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-overlay>
  </div>
</template>

<script>
import { BCol, BForm, BRow, BButton, BOverlay, BFormInput, BSkeleton, BAlert } from 'bootstrap-vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper';
import useService from '@/auth/service/useService';

export default {
  name: 'SalePriceAdditionalFeesForm',
  components: {
    BOverlay,
    BButton,
    BRow,
    BCol,
    BForm,
    BFormInput,
    ValidationObserver,
    ValidationProvider,
    BSkeleton,
    BAlert
  },

  mixins: [helper],

  data() {
    return {
      providerFees: [],
      additionalFees: [],
      submitting: false,
      loading: false
    };
  },

  async created() {
    await this.fetchAdditionalFees();
  },

  methods: {
    async fetchAdditionalFees() {
      try {
        this.loading = true;
        const params = {
          providerId: this.$route.params.providerId,
          customerId: this.$route.params.customerId
        };
        const response = await useService.pricingService.getSalePricesAdditionalFees(params);

        this.additionalFees = response.data;
      } catch (error) {
        // console.error('Error fetching additional fees:', error);
      } finally {
        this.loading = false;
      }
    },

    handlePriceTypeChange(service, value) {
      this.additionalFees = this.additionalFees.map(s => {
        if (s.id === service.id) {
          s.priceType = value;
          s.priceValue = null;
        }
        return s;
      });
    },

    getValidationRules() {
      const rules = ['required', 'doubleOrInteger', 'positive'];

      return rules.join('|');
    },

    submit() {
      this.$refs.additionalFeesForm.validate().then(success => {
        if (success) {
          const formData = {
            prices: this.additionalFees.map(fee => ({
              provider_additional_fee_id: fee.provider_additional_fee_id,
              salePrice: Number(fee.salePrice || 0)
            }))
          };

          this.submitting = true;

          useService.pricingService
            .saveSalePricesAdditionalFees({
              customerId: this.$route.params.customerId,
              providerId: this.$route.params.providerId,
              ...formData
            })
            .then(() => {
              this.showSuccessMessage(this.$t('Additional fees saved successfully'));
              this.$router.push({ name: 'sale-prices-additional-fees' });
            })
            .catch(error => {
              this.showErrorMessage(error.response.data.message);
            })
            .finally(() => {
              this.submitting = false;
            });
        }
      });
    }
  },

  setup() {
    const { t } = useI18nUtils();
    return { t };
  }
};
</script>

<style scoped>
.additional-fees-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.service-item {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 1rem;
  align-items: center;
  padding: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.service-item-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 1rem;
  border-radius: 4px;
  padding: 0 1rem;
}

.service-name {
  display: flex;
  align-items: center;
}

.service-name label {
  margin: 0;
  font-weight: 500;
}

.service-price-type,
.service-price-value {
  min-width: 200px;
}

.text-danger {
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}
</style>
